import Axios from 'axios';
import { compare } from 'compare-versions';
import { confirm } from '@mildang/design-system-legacy/esm/components/molecules/dialog/ConfirmDialog/';
import { buildVersionCheckJsonPath } from '@ui/constants/routes';
import { useCallback, useEffect, useRef } from 'react';
import { useInterval } from 'react-use';

const INTERVAL = 1000 * 60 * 20; // 20분

function clearCaches() {
  if (window.caches) {
    caches.keys().then((keyList) =>
      Promise.all(
        keyList.map((key) => {
          return caches.delete(key);
        }),
      ),
    );
  }
}

function unregistServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistration()
      .then(async (registration) => {
        clearCaches();
        await registration?.unregister();
        window.location.reload();
      })
      .catch((error) => {
        console.error(`Registration failed with ${error}`);
      });
  }
}

const useCheckClientVersion = (currentVersion: string) => {
  const isConfirmCreatedRef = useRef<boolean>(false);

  useEffect(() => {
    //@ts-ignore
    window.$version = currentVersion;
  }, [currentVersion]);

  const isNeedUpdate = useCallback(
    async function isNeedUpdate(): Promise<boolean> {
      try {
        const result = await Axios.get(buildVersionCheckJsonPath(), {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });

        const latestVersion = result.data;

        return compare(currentVersion, latestVersion, '<') || false;
      } catch (e) {
        return false;
      }
    },
    [currentVersion],
  );

  const renderConfirm = useCallback(async function renderConfirm() {
    return await confirm({
      title: '업데이트 알림',
      desc: (
        <>
          <span>최신 버전을 준비했어요. 아래 버튼을 누르면 </span>
          <b>1초만에 업데이트가 진행</b>
          <span>됩니다✨</span>
        </>
      ),
      confirmText: '업데이트',
      confirmButtonProps: {
        color: 'primary',
        onClick: (e) => {
          window.location.reload();
        },
      },
      dismissText: '나중에',
      dismissButtonProps: {
        onClick: () => {
          isConfirmCreatedRef.current = false;
        },
      },
      dialogProps: {
        onClose: () => {},
      },
    });
  }, []);

  const checkVersion = useCallback(async () => {
    if (isConfirmCreatedRef.current) return;
    const needUpdate = await isNeedUpdate();

    if (needUpdate) {
      try {
        await renderConfirm();
        isConfirmCreatedRef.current = true;
        unregistServiceWorker();
      } catch (error) {}
    }
  }, [isNeedUpdate, renderConfirm]);

  useEffect(() => {
    checkVersion();
  }, [checkVersion]);

  useInterval(async () => {
    checkVersion();
  }, INTERVAL);
};

export default useCheckClientVersion;
